import { arrayMove } from '@dnd-kit/sortable';
import { useEffect, useRef, useState } from 'react';
import useImageUpload from '../../../../hooks/useImageUpload';
import LangEnum from '../../../../lang/LangEnum';
import { readImageAsBase64 } from '../../../../lib/MediasLib';
import snackbarStore from '../../../../stores/Snackbar.store';
import { useTranslation } from '../../../translation/TranslationContext';
import { CatalogBannerListState, CatalogBannerPreview } from '../catalogCreationForm.types';
import { CATALOG_BANNERS_MAX_ITEMS } from '../catalogCreationMedia.consts';

interface UseCatalogBannerListParams {
  uploadWidth: number;
  initialUrls?: string[];
  onChange: (state: CatalogBannerListState) => void;
}

interface UploadIntoParams {
  key: number;
  file: File;
  previousUrl?: string;
}

export const useCatalogBannerList = ({
  uploadWidth,
  initialUrls = [],
  onChange,
}: UseCatalogBannerListParams) => {
  const { translate } = useTranslation();
  const { uploadImage } = useImageUpload(LangEnum.GLOBAL_ERROR_SENDING_IMAGE);

  const nextKeyRef = useRef(initialUrls.length);
  const hasChangedRef = useRef(false);
  const countRef = useRef(initialUrls.length);
  const [previews, setPreviews] = useState<CatalogBannerPreview[]>(() =>
    initialUrls.map((url, key) => ({ key, url, isUploading: false })),
  );

  useEffect(() => {
    countRef.current = previews.length;

    if (!hasChangedRef.current) {
      return;
    }

    onChange({
      urls: previews.filter((preview) => !preview.isUploading).map((preview) => preview.url),
      isUploading: previews.some((preview) => preview.isUploading),
    });
  }, [previews]);

  const updatePreview = (key: number, patch: Partial<CatalogBannerPreview>) => {
    setPreviews((current) =>
      current.map((preview) => (preview.key === key ? { ...preview, ...patch } : preview)),
    );
  };

  const rollback = (key: number, previousUrl?: string) => {
    if (previousUrl) {
      updatePreview(key, { url: previousUrl, isUploading: false });
      return;
    }

    setPreviews((current) => current.filter((preview) => preview.key !== key));
  };

  const uploadInto = async ({ key, file, previousUrl }: UploadIntoParams) => {
    try {
      const localPreviewUrl = await readImageAsBase64(file);

      if (localPreviewUrl) {
        updatePreview(key, { url: localPreviewUrl });
      }

      const uploadedImageUrl = await uploadImage(file, uploadWidth);

      if (!uploadedImageUrl) {
        rollback(key, previousUrl);
        return;
      }

      updatePreview(key, { url: uploadedImageUrl, isUploading: false });
    } catch {
      rollback(key, previousUrl);
      snackbarStore.handleMessage({
        type: 'error',
        message: translate(LangEnum.GLOBAL_ERROR_SENDING_IMAGE),
      });
    }
  };

  const addFiles = (files: File[]) => {
    const acceptedFiles = files.slice(0, CATALOG_BANNERS_MAX_ITEMS - countRef.current);

    if (acceptedFiles.length === 0) {
      return;
    }

    countRef.current += acceptedFiles.length;

    hasChangedRef.current = true;

    const newItems = acceptedFiles.map((file) => {
      nextKeyRef.current += 1;

      return {
        file,
        preview: { key: nextKeyRef.current, url: '', fileName: file.name, isUploading: true },
      };
    });

    setPreviews((current) => [...current, ...newItems.map(({ preview }) => preview)]);

    for (const { file, preview } of newItems) {
      uploadInto({ key: preview.key, file });
    }
  };

  const replaceFile = (key: number, file: File) => {
    const previous = previews.find((preview) => preview.key === key);

    if (!previous) {
      return;
    }

    hasChangedRef.current = true;
    updatePreview(key, { fileName: file.name, isUploading: true });
    uploadInto({ key, file, previousUrl: previous.url });
  };

  const removeFile = (key: number) => {
    hasChangedRef.current = true;
    setPreviews((current) => current.filter((preview) => preview.key !== key));
  };

  const moveFile = (oldIndex: number, newIndex: number) => {
    hasChangedRef.current = true;
    setPreviews((current) => arrayMove(current, oldIndex, newIndex));
  };

  return { previews, addFiles, removeFile, replaceFile, moveFile };
};
